import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useRef,
  useMemo,
  ReactNode
} from "react";
import { formatISO } from "date-fns";
import { useAPISubscription } from "@/services/api/subscription";
import {
  MessagePayload,
  MessagesResponse,
  MessageStatusUpdatePayload,
  SendMessageRequest,
  MAX_MESSAGES,
  UserEventFrame,
  UserEventHandler
} from "./types";
import { useAPIMutation } from "@/services/api/hook";
import { APIObject } from "@/services/api/types";
import { useAuth } from "@/services/auth/AuthContext";
import { ReservedUserIds } from "@/entities/users/types";
import {
  markRequestSent,
  measureResponseReceived,
  clearRequestMark,
  clearRequestMarks
} from "./latencyMarks";

const generateRequestId = (): string => {
  return crypto.randomUUID().replace(/-/g, "");
};

interface MessagingContextValue {
  messages: MessagePayload[];
  sendMessageViaWebSocket: (
    message: Omit<SendMessageRequest, "requestId">
  ) => string;
  cancelPendingRequest: (requestId: string) => void;
  isConnected: boolean;
  updateMessageStatus: (
    messageId: string,
    updates: MessageStatusUpdatePayload
  ) => void;
  setOnThoughtMessage: (
    callback: ((thought: string) => void) | undefined
  ) => void;
  setOnResponseReceived: (
    callback: ((requestId: string) => void) | undefined
  ) => void;
  setOnQuestionResent: (
    callback: ((questionId: string) => void) | undefined
  ) => void;
  subscribeToUserEvent: (
    eventType: string,
    handler: UserEventHandler
  ) => () => void;
}

const isUserEventFrame = (frameData: unknown): frameData is UserEventFrame => {
  if (typeof frameData !== "object" || frameData === null) return false;
  const event = (frameData as { event?: unknown }).event;
  return (
    typeof event === "object" &&
    event !== null &&
    typeof (event as { type?: unknown }).type === "string"
  );
};

const isUserAuthored = (message: MessagePayload): boolean =>
  !!message.fromUserId &&
  !Object.values(ReservedUserIds).includes(
    message.fromUserId as ReservedUserIds
  );

const MessagingContext = createContext<MessagingContextValue | null>(null);

const useUpdateMessageStatusMutation = () => {
  return useAPIMutation<
    {
      pathParams: { messageId: string };
      data: MessageStatusUpdatePayload;
    },
    APIObject
  >("messaging/id", {
    method: "PUT"
  });
};

interface MessagingProviderProps {
  children: ReactNode;
}

export const MessagingProvider: React.FC<MessagingProviderProps> = ({
  children
}) => {
  const [messages, setMessages] = useState<MessagePayload[]>([]);
  const { token: authToken, tokenExpiry } = useAuth();
  const { mutate: mutateMessageStatus } = useUpdateMessageStatusMutation();
  const pendingRequestsRef = useRef<Set<string>>(new Set());

  // Callback refs to allow components to register their handlers
  const onThoughtMessageRef = useRef<((thought: string) => void) | undefined>();
  const onResponseReceivedRef = useRef<
    ((requestId: string) => void) | undefined
  >();
  const onQuestionResentRef = useRef<
    ((questionId: string) => void) | undefined
  >();
  // Subscribers to user-event frames, by event type. A type nobody subscribes
  // to is dropped, so a server that sends a new kind of event does not break a
  // client that predates it.
  const userEventHandlersRef = useRef<Map<string, Set<UserEventHandler>>>(
    new Map()
  );

  const subscriptionOptions = useMemo(() => ({
    onMessage: (
      messagesResponse: MessagesResponse | { thought?: string } | UserEventFrame
    ) => {
      if (isUserEventFrame(messagesResponse)) {
        const { type, payload } = messagesResponse.event;
        userEventHandlersRef.current
          .get(type)
          ?.forEach((handler) => {
            // One failing subscriber must not keep the event from the rest.
            try {
              handler(payload);
            } catch {
              console.error("User event handler failed");
            }
          });
        return;
      }

      // Check if this is a thought message
      if (
        typeof messagesResponse === "object" &&
        messagesResponse !== null &&
        "thought" in messagesResponse &&
        messagesResponse.thought
      ) {
        onThoughtMessageRef.current?.(messagesResponse.thought);
        return;
      }

      // Regular message handling - only process if it has messages array
      const messageResponse = messagesResponse as MessagesResponse;
      if (messageResponse.messages && Array.isArray(messageResponse.messages)) {
        const responseRequestId = messageResponse.requestId;

        // The user's own question under a request id is that question being
        // retried, re-sent the moment the retry starts. It is not the answer.
        const resentQuestions = responseRequestId
          ? messageResponse.messages.filter(isUserAuthored)
          : [];
        resentQuestions.forEach((question) =>
          onQuestionResentRef.current?.(question.id)
        );
        const isAnswer =
          resentQuestions.length < messageResponse.messages.length;

        // Only clear thought message when we receive a response to our pending request (Dreamer response)
        // Don't clear for other messages like mentor alerts
        if (
          isAnswer &&
          responseRequestId &&
          pendingRequestsRef.current.has(responseRequestId)
        ) {
          onThoughtMessageRef.current?.("");
          pendingRequestsRef.current.delete(responseRequestId);
          measureResponseReceived(responseRequestId);
          onResponseReceivedRef.current?.(responseRequestId);
        }

        setMessages((prevMessages) => {
          // Check if there are actually new messages to add
          const existingIds = new Set(prevMessages.map((message) => message.id));
          const actuallyNewMessages = messageResponse.messages.filter(
            (message) => !existingIds.has(message.id)
          );

          // A regenerated draft is not a new message: the server appends a
          // version and re-sends the message it belongs to, under the same id.
          // Deduplication alone would drop it and the card would never update.
          const redeliveredDrafts = new Map(
            messageResponse.messages
              .filter(
                (message) =>
                  existingIds.has(message.id) && !!message.emailDraft
              )
              .map((message) => [message.id, message])
          );

          const updatedMessages =
            redeliveredDrafts.size === 0
              ? prevMessages
              : prevMessages.map((message) => {
                  const redelivered = redeliveredDrafts.get(message.id);
                  if (!redelivered) return message;
                  // `message` moves with the draft, not just alongside it: a regenerate
                  // re-renders Dreamer's opening for the new version and the server writes
                  // it back to the same row, so keeping v1's text here would leave the
                  // intro above the card stale until the next reload contradicted it.
                  return {
                    ...message,
                    message: redelivered.message,
                    emailDraft: redelivered.emailDraft
                  };
                });

          // If no new messages, return the same array reference to prevent re-renders
          if (actuallyNewMessages.length === 0) {
            return updatedMessages;
          }

          const newMessages = [...updatedMessages, ...actuallyNewMessages];
          return newMessages.slice(-MAX_MESSAGES);
        });
      }
    },
    // The socket auto-reconnects indefinitely (see subscription.tsx) and
    // heartbeats routinely trigger this, so a request that was pending when
    // it fired can still get a real response once reconnected — clear its
    // latency mark only, and leave it in pendingRequestsRef so the thought
    // indicator and onResponseReceived still fire when that response lands.
    onError: () => {
      clearRequestMarks(pendingRequestsRef.current);
    }
  }), []);

  const { isConnected, sendMessage: sendWebSocketMessage } = useAPISubscription(
    "messaging/ws",
    subscriptionOptions
  );

  const sendMessageViaWebSocket = useCallback(
    (message: Omit<SendMessageRequest, "requestId" | "currentDatetime" | "authToken">): string => {
      if (tokenExpiry > 0 && tokenExpiry < Date.now()) {
        return "";
      }

      const requestId = generateRequestId();
      pendingRequestsRef.current.add(requestId);
      markRequestSent(requestId);
      sendWebSocketMessage({
        ...message,
        requestId,
        currentDatetime: formatISO(new Date()),
        ...(authToken ? { authToken } : {})
      });
      return requestId;
    },
    [sendWebSocketMessage, authToken, tokenExpiry]
  );

  const cancelPendingRequest = useCallback(
    (requestId: string) => {
      pendingRequestsRef.current.delete(requestId);
      clearRequestMark(requestId);
      onThoughtMessageRef.current?.("");
      sendWebSocketMessage({
        message: "",
        cancelRequestId: requestId,
        ...(authToken ? { authToken } : {})
      });
    },
    [sendWebSocketMessage, authToken]
  );

  // A provider unmount abandons every still-pending request the same way a
  // WebSocket error does — no response, cancel, or error handler will ever
  // fire for it — so clear their marks here too rather than leaving them for
  // the TTL sweep on some later send.
  useEffect(() => {
    return () => {
      clearRequestMarks(pendingRequestsRef.current);
      pendingRequestsRef.current.clear();
    };
  }, []);

  const mutateMessageStatusRef = useRef(mutateMessageStatus);
  mutateMessageStatusRef.current = mutateMessageStatus;

  const updateMessageStatus = useCallback(
    (messageId: string, updates: MessageStatusUpdatePayload) => {
      const currentMessage = messages.find(
        (msg) => msg.messageId === messageId
      );
      const revertData = {
        isStarred:
          updates.isStarred !== undefined
            ? currentMessage?.isStarred
            : undefined,
        isLiked:
          updates.isLiked !== undefined ? currentMessage?.isLiked : undefined
      };

      setMessages((prevMessages) =>
        prevMessages.map((msg) =>
          msg.messageId === messageId ? { ...msg, ...updates } : msg
        )
      );

      mutateMessageStatusRef.current(
        {
          pathParams: { messageId },
          data: updates
        },
        {
          onSuccess: () => {},
          onError: () => {
            setMessages((prevMessages) =>
              prevMessages.map((msg) =>
                msg.messageId === messageId ? { ...msg, ...revertData } : msg
              )
            );
          }
        }
      );
    },
    [messages]
  );

  const setOnThoughtMessage = useCallback(
    (callback: ((thought: string) => void) | undefined) => {
      onThoughtMessageRef.current = callback;
    },
    []
  );

  const setOnResponseReceived = useCallback(
    (callback: ((requestId: string) => void) | undefined) => {
      onResponseReceivedRef.current = callback;
    },
    []
  );

  const setOnQuestionResent = useCallback(
    (callback: ((questionId: string) => void) | undefined) => {
      onQuestionResentRef.current = callback;
    },
    []
  );

  const subscribeToUserEvent = useCallback(
    (eventType: string, handler: UserEventHandler) => {
      const handlers =
        userEventHandlersRef.current.get(eventType) ?? new Set();
      handlers.add(handler);
      userEventHandlersRef.current.set(eventType, handlers);
      return () => {
        handlers.delete(handler);
        if (handlers.size === 0) {
          userEventHandlersRef.current.delete(eventType);
        }
      };
    },
    []
  );

  const value = useMemo(
    () => ({
      messages,
      sendMessageViaWebSocket,
      cancelPendingRequest,
      isConnected,
      updateMessageStatus,
      setOnThoughtMessage,
      setOnResponseReceived,
      setOnQuestionResent,
      subscribeToUserEvent
    }),
    [
      messages,
      sendMessageViaWebSocket,
      cancelPendingRequest,
      isConnected,
      updateMessageStatus,
      setOnThoughtMessage,
      setOnResponseReceived,
      setOnQuestionResent,
      subscribeToUserEvent
    ]
  );

  return (
    <MessagingContext.Provider value={value}>
      {children}
    </MessagingContext.Provider>
  );
};

export const useMessagingContext = (): MessagingContextValue => {
  const context = useContext(MessagingContext);
  if (!context) {
    throw new Error(
      "useMessagingContext must be used within a MessagingProvider"
    );
  }
  return context;
};

/**
 * Calls `handler` with the payload of every user-event frame of `eventType`
 * the socket delivers while the caller is mounted. The latest `handler` is
 * always the one called, so it need not be memoised.
 */
export const useUserEvent = <Payload,>(
  eventType: string,
  handler: (payload: Payload) => void
) => {
  const { subscribeToUserEvent } = useMessagingContext();
  const handlerRef = useRef(handler);
  // Written after commit, not during render.
  useEffect(() => {
    handlerRef.current = handler;
  });

  useEffect(
    () =>
      subscribeToUserEvent(eventType, (payload) =>
        handlerRef.current(payload as Payload)
      ),
    [subscribeToUserEvent, eventType]
  );
};
